import { useCallback, useState, type FC, useEffect } from 'react';
import { Button } from '@hologram-dimension/button';
import { Form } from '@hologram-dimension/form';
import { Link } from '@hologram-dimension/link';
import { TextInputField } from '@hologram-dimension/text-input-field';
import { CheckboxField } from '@hologram-dimension/checkbox-field';
import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Loader } from '@hologram-dimension/loader';
import logoWhiteWithText from 'assets/img/logo/hologram_logo_white.webp?&imagetools';
import logoBlackWithText from 'assets/img/logo/hologram_logo_black.webp?&imagetools';
import { SubmitHandler, useForm, FormProvider } from 'react-hook-form';
import { RegisterAccountParams, RegisterResponse } from 'types/legacyDashboard';
import Picture from 'components/Picture/Picture';
import PasswordEntry from 'components/PasswordEntry/PasswordEntry';
import { Label } from '@hologram-dimension/label';
import styles from './Register.module.css';

const IconCheckmark: FC = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="20" height="20" rx="10" fill="#BFFD11" />
    <path
      d="M14.5469 6.03125C14.7875 6.0313 14.9967 6.11959 15.1739 6.29688C15.351 6.47395 15.4394 6.68402 15.4395 6.92676C15.4395 7.16966 15.3511 7.38033 15.1739 7.55762L9.06645 13.6719C8.88912 13.8492 8.68005 13.9385 8.4395 13.9385C8.19896 13.9385 7.98987 13.8492 7.81254 13.6719L4.96489 10.8242C4.78772 10.647 4.69987 10.4371 4.70219 10.1943C4.70464 9.95134 4.79537 9.7407 4.9727 9.56348C5.14987 9.38632 5.3597 9.29785 5.60258 9.29785C5.84557 9.29786 6.05612 9.38615 6.23344 9.56348L8.4395 11.7773L13.92 6.29688C14.0972 6.11954 14.3064 6.03125 14.5469 6.03125Z"
      fill="#00040F"
    />
  </svg>
);

type HubspotParams = Omit<RegisterAccountParams, 'password'> & {
  userId: number;
  marketing: boolean;
  userIsNew: boolean;
  password?: never; // Explicitly prevent password from being included
};
interface RegisterFormFields {
  firstName: string;
  lastName: string;
  company: string;
  email: string;
  phoneCountryCode: string;
  phone: string;
  password: string;
  terms: boolean;
  marketing: boolean;
}

const Register: FC<{
  loginUrl: string;
  orgInviteId?: string;
  orgInviteToken?: string;
  email?: string;
  onSubmit: (params: RegisterAccountParams) => Promise<RegisterResponse>;
}> = ({ loginUrl, orgInviteId, orgInviteToken, email, onSubmit: onSubmitProp }) => {
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [queryString, setQueryString] = useState('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setQueryString(window.location.search);
    }
  }, []);

  const loginUrlWithQuerystring = `${loginUrl}${queryString}`;

  const userIsNew = queryString.includes('userisnew=1');

  const methods = useForm<RegisterFormFields>({
    defaultValues: {
      email: email || '',
    },
    mode: 'onBlur',
  });

  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    setValue,
  } = methods;

  const submitHubspot = useCallback((data: HubspotParams) => {
    fetch('https://hooks.zapier.com/hooks/catch/2922077/2v4sb3v/', {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...data, password: undefined }), // to make extra sure password is never sent
    }).catch((error) => {
      // Log for analytics/debugging but don't block user flow
      // eslint-disable-next-line no-console
      console.error('Marketing data submission failed:', error);
    });
  }, []);

  const onSubmit = useCallback<SubmitHandler<RegisterFormFields>>(
    async (data) => {
      if (isValid && !isRegistering) {
        setIsRegistering(true);
        setRegistrationError(null);

        try {
          const submissionData: RegisterAccountParams = {
            email: data.email,
            firstName: data.firstName,
            lastName: data.lastName,
            company: data.company,
            phone: data.phone ? `${data.phoneCountryCode}${data.phone}` : '',
            password: data.password,
            terms: data.terms,
            orgInviteId: orgInviteId || '',
            orgInviteToken: orgInviteToken || '',
          };

          const result = await onSubmitProp(submissionData);

          if (result.success) {
            setSuccess(true);
            submitHubspot({
              email: submissionData.email,
              firstName: submissionData.firstName,
              lastName: submissionData.lastName,
              company: submissionData.company,
              phone: submissionData.phone,
              terms: submissionData.terms,
              orgInviteId: submissionData.orgInviteId,
              orgInviteToken: submissionData.orgInviteToken,
              userId: result.userId,
              marketing: data.marketing,
              userIsNew: !orgInviteId || userIsNew,
            });
            // Redirect user - NOTE: The parent component handles navigation
          }
        } catch (error) {
          setRegistrationError(
            error instanceof Error ? error.message : 'Registration failed. Please try again.'
          );
        } finally {
          setIsRegistering(false);
        }
      }
    },
    [onSubmitProp, isValid, orgInviteId, orgInviteToken, submitHubspot, isRegistering, userIsNew]
  );

  return (
    <div className={styles.wrapper}>
      <div className={styles.aside}>
        <Picture
          image={logoWhiteWithText}
          alt="Hologram logo"
          className={styles.logo}
          setDimensions={false}
        />
        <img
          className={styles.illustration}
          alt="Illustration of charts in the Hologram dashboard"
          loading="lazy"
          width="2200"
          height="2200"
          decoding="async"
          data-nimg="1"
          style={{ color: 'transparent' }}
          sizes="(min-width: 640px) 640px, 100vw"
          srcSet="https://cdn.sanity.io/images/14xthjfi/prod/bc025d57987624b910b0f609e4d9c115fcfeb8b6-2200x2200.png?w=320&amp;q=75&amp;fit=clip&amp;auto=format 320w, https://cdn.sanity.io/images/14xthjfi/prod/bc025d57987624b910b0f609e4d9c115fcfeb8b6-2200x2200.png?w=384&amp;q=75&amp;fit=clip&amp;auto=format 384w, https://cdn.sanity.io/images/14xthjfi/prod/bc025d57987624b910b0f609e4d9c115fcfeb8b6-2200x2200.png?w=480&amp;q=75&amp;fit=clip&amp;auto=format 480w, https://cdn.sanity.io/images/14xthjfi/prod/bc025d57987624b910b0f609e4d9c115fcfeb8b6-2200x2200.png?w=550&amp;q=75&amp;fit=clip&amp;auto=format 550w, https://cdn.sanity.io/images/14xthjfi/prod/bc025d57987624b910b0f609e4d9c115fcfeb8b6-2200x2200.png?w=720&amp;q=75&amp;fit=clip&amp;auto=format 720w, https://cdn.sanity.io/images/14xthjfi/prod/bc025d57987624b910b0f609e4d9c115fcfeb8b6-2200x2200.png?w=1024&amp;q=75&amp;fit=clip&amp;auto=format 1024w, https://cdn.sanity.io/images/14xthjfi/prod/bc025d57987624b910b0f609e4d9c115fcfeb8b6-2200x2200.png?w=1200&amp;q=75&amp;fit=clip&amp;auto=format 1200w, https://cdn.sanity.io/images/14xthjfi/prod/bc025d57987624b910b0f609e4d9c115fcfeb8b6-2200x2200.png?w=1600&amp;q=75&amp;fit=clip&amp;auto=format 1600w"
          src="https://cdn.sanity.io/images/14xthjfi/prod/bc025d57987624b910b0f609e4d9c115fcfeb8b6-2200x2200.png?w=1600&amp;q=75&amp;fit=clip&amp;auto=format"
        />
      </div>

      <div className={styles.formContainer}>
        {success && (
          <Loader>
            We&apos;re building your IoT dashboard. You&apos;ll be online in just a moment.
          </Loader>
        )}
        {!success && (
          <FormProvider {...methods}>
            <Form
              footerActions={
                <div className={styles.footerActions}>
                  <Button
                    type="submit"
                    iconEnd="ArrowEastShort"
                    loading={isRegistering}
                    disabled={isRegistering}
                  >
                    Create account
                  </Button>
                  <p className={styles.loginLink}>
                    Already have an account? <Link to={loginUrlWithQuerystring}>Sign in</Link>
                  </p>
                </div>
              }
              onSubmit={handleSubmit(onSubmit)}
            >
              <Picture
                image={logoBlackWithText}
                alt="Hologram logo"
                className={styles.logo}
                setDimensions={false}
              />
              <div className={styles.formHeading}>
                <h1>Create a free account</h1>
                <p>Hologram connects IoT devices of every kind and size.</p>
                <ul>
                  <li>
                    <IconCheckmark /> Get online and stay online, anywhere in the world.
                  </li>
                  <li>
                    <IconCheckmark /> Maintain visibility into data usage and behavior, and take
                    action.
                  </li>
                  <li>
                    <IconCheckmark /> Optimize your fleet with real-time insights.
                  </li>
                </ul>
              </div>
              {registrationError && (
                <BannerNotification variant="error">{registrationError}</BannerNotification>
              )}
              <div className={styles.fieldRow}>
                <TextInputField
                  label="Your first name"
                  {...register('firstName', { required: 'Please enter a first and last name' })}
                  validationMessage={errors.firstName?.message}
                  invalid={!!errors.firstName}
                />
                <TextInputField
                  label="Your last name"
                  {...register('lastName', { required: 'Please enter a first and last name' })}
                  validationMessage={errors.lastName?.message}
                  invalid={!!errors.lastName}
                />
              </div>
              {!(orgInviteId && orgInviteToken) && (
                <TextInputField
                  label="Your company name"
                  helpText="What do you want to call the organization you will see in your dashboard?"
                  {...register('company', {
                    validate: (value) => {
                      if (!!orgInviteId && !!orgInviteToken) {
                        return true;
                      }
                      return !!value || 'Please enter a company name';
                    },
                  })}
                  validationMessage={errors.company?.message}
                  invalid={!!errors.company}
                />
              )}
              <TextInputField
                label="Your work email"
                {...register('email', {
                  required: 'Please enter a work email',
                })}
                validationMessage={errors.email?.message}
                invalid={!!errors.email}
              />
              {!(orgInviteId && orgInviteToken) && (
                <div className={styles.phoneContainer}>
                  <Label htmlFor="phone-field" id="phone-label">
                    Your phone number
                  </Label>
                  <fieldset
                    className={styles.phoneRow}
                    aria-describedby={
                      errors.phoneCountryCode || errors.phone ? 'phone-error' : undefined
                    }
                    id="phone-field"
                    aria-labelledby="phone-label"
                  >
                    <TextInputField
                      label="Country code"
                      {...register('phoneCountryCode', {
                        pattern: {
                          value: /^\+[0-9]{1,3}$/,
                          message: 'Please enter a valid country code',
                        },
                        validate: (value) => {
                          if (!!orgInviteId && !!orgInviteToken) {
                            return true;
                          }
                          return !!value || 'Please enter a country code';
                        },
                      })}
                      className={styles.phoneCountryCode}
                      hiddenLabel
                      invalid={!!errors.phoneCountryCode}
                      defaultValue="+1"
                      onChange={(e) => {
                        const { value } = e.target as HTMLInputElement;
                        if (value.length > 0 && value[0] !== '+') {
                          setValue('phoneCountryCode', `+${value}`, { shouldValidate: true });
                        }
                      }}
                    />
                    <TextInputField
                      label="Your phone number"
                      {...register('phone', {
                        pattern: {
                          value: /^[0-9+\-() ]*$/,
                          message: 'Please enter a valid phone number',
                        },
                        validate: (value) => {
                          if (!!orgInviteId && !!orgInviteToken) {
                            return true;
                          }
                          return !!value || 'Please enter a phone number';
                        },
                      })}
                      className={styles.phoneNumber}
                      hiddenLabel
                      invalid={!!errors.phone}
                    />
                  </fieldset>
                  {(errors.phoneCountryCode || errors.phone) && (
                    <p id="phone-error" className={styles.phoneError}>
                      {errors.phoneCountryCode?.message || errors.phone?.message}
                    </p>
                  )}
                </div>
              )}
              <PasswordEntry showValidation />
              <div className={styles.checkboxFields}>
                <CheckboxField
                  options={[
                    {
                      label: "I agree to Hologram's Terms of Use and Privacy Policy.",
                      labelNode: (
                        <span>
                          I agree to Hologram&rsquo;s{' '}
                          <Link
                            type="inline"
                            target="_blank"
                            rel="noreferrer"
                            href="https://www.hologram.io/terms-of-use/"
                          >
                            Terms of Use
                          </Link>{' '}
                          and{' '}
                          <Link
                            type="inline"
                            target="_blank"
                            rel="noreferrer"
                            href="https://www.hologram.io/privacy-policy/"
                          >
                            Privacy Policy
                          </Link>
                          .
                        </span>
                      ),
                    },
                  ]}
                  label="I agree to Hologram's Terms of Use and Privacy Policy."
                  hiddenLabel
                  invalid={!!errors.terms}
                  validationMessage={errors.terms?.message}
                  {...register('terms', {
                    required: 'Please agree to our Terms of Use and Privacy Policy',
                  })}
                />
                <CheckboxField
                  options={[
                    {
                      label:
                        "Yes, I'd like to receive personalized marketing communications from Hologram about its products, solutions, and events via the email address I've provided. I understand that I can opt out at any time and that my information will be processed according to Hologram's Privacy Policy. (optional)",
                      labelNode: (
                        <span>
                          Yes, I&rsquo;d like to receive personalized marketing communications from
                          Hologram about its products, solutions, and events via the email address
                          I&rsquo;ve provided. I understand that I can opt out at any time and that
                          my information will be processed according to Hologram&rsquo;s{' '}
                          <Link
                            type="inline"
                            target="_blank"
                            rel="noreferrer"
                            href="https://www.hologram.io/privacy-policy"
                          >
                            Privacy Policy
                          </Link>
                          . <span className={styles.diminished}>(optional)</span>
                        </span>
                      ),
                    },
                  ]}
                  label="Marketing communications"
                  hiddenLabel
                  invalid={!!errors.marketing}
                  validationMessage={errors.marketing?.message}
                  {...register('marketing')}
                />
              </div>
            </Form>
          </FormProvider>
        )}
      </div>
    </div>
  );
};

export default Register;
